import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Activity, CalendarDays, LoaderCircle, UsersRound } from "lucide-react";
import {
  getAllBDMs,
  getBDMBoardData,
  getLogsCount,
} from "../../store/slices/telecallerLeadSlice";
import BDMBoard from "./BDMBoard";
import { RootDispatch, RootState } from "../../store";
import dayjs from "dayjs";
import { ITelecaller } from "../../models/LeadData.model";
import DataEntryPerformance from "../../components/Lead/DataEntryPerformance";
import GPHierarchyDashboard from "../AdminDashboard/GPHierarchyDashboard";
import ProductStockPageByWarehouse from "../AdminDashboard/ProductStockPageByWarehouse";
import StoreStockPageByWarehouse from "../AdminDashboard/StoreStockPageByWarehouse";
import ProductionCostPage from "../AdminDashboard/ProductionCostPage";
import CombinedStockDownloadButton from "../AdminDashboard/CombinedStockDownloadButton";
import LossReportModal from "../AdminDashboard/LossReportModal";

type StockTab = "product" | "raw" | "gp" | "cost";

const getDateParam = (
  searchParams: URLSearchParams,
  key: string,
  fallback: string,
): string => {
  const value = searchParams.get(key);
  return value &&
    dayjs(value).isValid() &&
    dayjs(value).format("YYYY-MM-DD") === value
    ? value
    : fallback;
};

const getOptionalDateParam = (
  searchParams: URLSearchParams,
  key: string,
): string | undefined => {
  const value = searchParams.get(key);
  return value &&
    dayjs(value).isValid() &&
    dayjs(value).format("YYYY-MM-DD") === value
    ? value
    : undefined;
};

const AdminBDMDashboard: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBdmId, setSelectedBdmId] = useState<string | null>(
    () => searchParams.get("bdm") || null,
  );

  const [bdmStartDate, setBdmStartDate] = useState<string>(() =>
    getDateParam(searchParams, "bdmStart", dayjs().format("YYYY-MM-DD")),
  );
  const [bdmEndDate, setBdmEndDate] = useState<string | undefined>(() =>
    getOptionalDateParam(searchParams, "bdmEnd"),
  );
  const [logsStart, setLogsStart] = useState(() =>
    getDateParam(searchParams, "logsStart", dayjs().format("YYYY-MM-DD")),
  );
  const [logsEnd, setLogsEnd] = useState<string | undefined>(() =>
    getOptionalDateParam(searchParams, "logsEnd"),
  );
  const [dataEntryStart, setDataEntryStart] = useState<string>(() =>
    getDateParam(searchParams, "entryStart", dayjs().format("YYYY-MM-DD")),
  );
  const [dataEntryEnd, setDataEntryEnd] = useState<string | undefined>(() =>
    getOptionalDateParam(searchParams, "entryEnd"),
  );

  const [globalStockDate, setGlobalStockDate] = useState<string>(() =>
    getDateParam(searchParams, "stockDate", dayjs().format("YYYY-MM-DD")),
  );

  const [isLossReportModalOpen, setIsLossReportModalOpen] = useState(false);

  const {
    list: bdms,
    loadingList,
    loadingBoard,
  } = useSelector((state: RootState) => state.telecallerLead);
  const { dataEntryLeadCount, loading } = useSelector(
    (state: RootState) => state.kanban,
  );
  const { role } = useSelector((state: RootState) => state.auth);

  const [activeStockTab, setActiveStockTab] = useState<StockTab>(() => {
    const tab = searchParams.get("stockTab");
    return tab === "raw" || tab === "gp" || tab === "cost" ? tab : "product";
  });

  const updateDashboardParams = (
    updates: Record<string, string | undefined>,
  ) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    setSelectedBdmId(searchParams.get("bdm") || null);
    setBdmStartDate(
      getDateParam(searchParams, "bdmStart", dayjs().format("YYYY-MM-DD")),
    );
    setBdmEndDate(getOptionalDateParam(searchParams, "bdmEnd"));
    setLogsStart(
      getDateParam(searchParams, "logsStart", dayjs().format("YYYY-MM-DD")),
    );
    setLogsEnd(getOptionalDateParam(searchParams, "logsEnd"));
    setDataEntryStart(
      getDateParam(searchParams, "entryStart", dayjs().format("YYYY-MM-DD")),
    );
    setDataEntryEnd(getOptionalDateParam(searchParams, "entryEnd"));
    setGlobalStockDate(
      getDateParam(searchParams, "stockDate", dayjs().format("YYYY-MM-DD")),
    );
    const tab = searchParams.get("stockTab");
    setActiveStockTab(
      tab === "raw" || tab === "gp" || tab === "cost" ? tab : "product",
    );
  }, [searchParams]);

  useEffect(() => {
    dispatch(getLogsCount({ startDate: logsStart, endDate: logsEnd })).unwrap();
  }, [dispatch, logsStart, logsEnd]);

  useEffect(() => {
    dispatch(getAllBDMs({ startDate: bdmStartDate, endDate: bdmEndDate }));
  }, [dispatch, bdmStartDate, bdmEndDate]);

  useEffect(() => {
    if (selectedBdmId) {
      dispatch(
        getBDMBoardData({
          bdmId: selectedBdmId,
          startDate: logsStart,
          endDate: logsEnd,
        }),
      );
    }
  }, [selectedBdmId, logsStart, logsEnd, dispatch]);

  const handleBdmDateChange = (start: string, end?: string) => {
    setBdmStartDate(start);
    setBdmEndDate(end);
    updateDashboardParams({ bdmStart: start, bdmEnd: end });
  };

  const handleLogsDateChange = (start: string, end?: string) => {
    setLogsStart(start);
    setLogsEnd(end);
    updateDashboardParams({ logsStart: start, logsEnd: end });
  };

  const handleDataEntryDateChange = (start: string, end?: string) => {
    setDataEntryStart(start);
    setDataEntryEnd(end);
    updateDashboardParams({ entryStart: start, entryEnd: end });
  };

  const handleStockTabChange = (tab: StockTab) => {
    setActiveStockTab(tab);
    updateDashboardParams({ stockTab: tab });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-4 sm:px-5 sm:py-6 xl:px-8">
      <div className="mx-auto max-w-[1800px]">
        <div className="mb-5 flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">
              Operations
            </p>
            <h1 className="text-2xl font-bold text-slate-900">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setIsLossReportModalOpen(true)}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 sm:w-auto"
            >
              <Activity size={17} aria-hidden="true" />
              Monthly Loss Report
            </button>
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] xl:gap-5">
          <aside className="min-w-0 space-y-4">
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between xl:flex-col xl:items-stretch">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                      <UsersRound size={18} aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="text-base font-semibold text-slate-900">
                        BDM users
                      </h2>
                      <p className="text-xs text-slate-500">
                        {bdms.length} active in this range
                      </p>
                    </div>
                  </div>
                  {loadingList && (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700"
                      role="status"
                      aria-live="polite"
                    >
                      <LoaderCircle
                        size={14}
                        className="animate-spin"
                        aria-hidden="true"
                      />
                      Updating
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="min-w-0 text-[11px] font-medium text-slate-500">
                    From
                    <input
                      id="bdmStartDate"
                      type="date"
                      value={bdmStartDate}
                      onChange={(e) => handleBdmDateChange(e.target.value)}
                      className="mt-1 block h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                    />
                  </label>
                  <label className="min-w-0 text-[11px] font-medium text-slate-500">
                    To
                    <input
                      id="bdmEndDate"
                      type="date"
                      value={bdmEndDate || ""}
                      onChange={(e) =>
                        handleBdmDateChange(
                          bdmStartDate,
                          e.target.value || undefined,
                        )
                      }
                      className="mt-1 block h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                    />
                  </label>
                </div>
              </div>

              {bdms.length === 0 && loadingList ? (
                <div
                  className="flex min-h-32 items-center justify-center gap-2 text-sm text-slate-500"
                  role="status"
                >
                  <LoaderCircle
                    size={17}
                    className="animate-spin text-teal-700"
                    aria-hidden="true"
                  />
                  Loading BDM users
                </div>
              ) : bdms.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">
                  No BDM users found for this date range.
                </p>
              ) : (
                <ul className="max-h-[min(58vh,640px)] divide-y divide-slate-100 overflow-y-auto">
                  {bdms.map((bdm: ITelecaller) => (
                    <li
                      key={bdm._id}
                      onClick={() => {
                        setSelectedBdmId(bdm._id);
                        updateDashboardParams({ bdm: bdm._id });
                      }}
                      className={`cursor-pointer border-l-[3px] px-3 py-3 transition-colors hover:bg-teal-50/60 ${
                        selectedBdmId === bdm._id
                          ? "border-teal-700 bg-teal-50/70"
                          : "border-transparent"
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-100">
                          <span className="text-xs font-semibold text-slate-700">
                            {bdm.username?.charAt(0).toUpperCase() || "?"}
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {bdm.username}
                          </p>
                          <div className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700">
                            <svg
                              className="h-3.5 w-3.5 text-teal-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            {bdm.leadLogCount ?? 0}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <DataEntryPerformance
              startDate={dataEntryStart}
              endDate={dataEntryEnd}
              onDateChange={handleDataEntryDateChange}
              data={dataEntryLeadCount}
              loading={loading}
              keepPreviousData
            />
          </aside>

          <section className="relative min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {selectedBdmId ? (
              <BDMBoard
                isAdminView={true}
                adminStartDate={logsStart}
                adminEndDate={logsEnd}
                onDateChange={handleLogsDateChange}
              />
            ) : (
              <div className="flex min-h-72 flex-col items-center justify-center p-6 text-center text-slate-500">
                <svg
                  className="mb-4 h-14 w-14 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  />
                </svg>
                <p className="text-base font-semibold text-slate-700">
                  Select a BDM to view their board
                </p>
                <p className="mt-1 text-sm">
                  Choose a user from the list to get started.
                </p>
              </div>
            )}
            {loadingBoard && selectedBdmId && (
              <div
                className="pointer-events-none absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-teal-100 bg-white/95 px-3 py-1.5 text-xs font-medium text-teal-800 shadow-sm"
                role="status"
                aria-live="polite"
              >
                <LoaderCircle
                  size={14}
                  className="animate-spin"
                  aria-hidden="true"
                />
                Updating board
              </div>
            )}
          </section>
        </div>

        {role === "admin" && (
          <div className="mt-6">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-gray-200">
                <nav
                  className="flex flex-col gap-2 p-2 lg:flex-row lg:items-center lg:justify-between"
                  aria-label="Stock and reporting sections"
                >
                  <div className="flex min-w-0 -mb-px overflow-x-auto">
                    <button
                      onClick={() => handleStockTabChange("product")}
                      className={`min-h-11 border-b-2 px-4 text-sm font-medium transition-colors whitespace-nowrap sm:px-5 ${
                        activeStockTab === "product"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      Finished Goods
                    </button>

                    <button
                      onClick={() => handleStockTabChange("raw")}
                      className={`min-h-11 border-b-2 px-4 text-sm font-medium transition-colors whitespace-nowrap sm:px-5 ${
                        activeStockTab === "raw"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      Store Stock
                    </button>

                    <button
                      onClick={() => handleStockTabChange("gp")}
                      className={`min-h-11 border-b-2 px-4 text-sm font-medium transition-colors whitespace-nowrap sm:px-5 ${
                        activeStockTab === "gp"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      GP Hierarchy
                    </button>

                    <button
                      onClick={() => handleStockTabChange("cost")}
                      className={`min-h-11 border-b-2 px-4 text-sm font-medium transition-colors whitespace-nowrap sm:px-5 ${
                        activeStockTab === "cost"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      Production Cost
                    </button>
                  </div>

                  {(activeStockTab === "product" ||
                    activeStockTab === "raw") && (
                    <div className="flex flex-wrap items-center justify-between gap-3 py-2 lg:ml-auto lg:justify-end">
                      <div className="flex items-center gap-2">
                        <CalendarDays
                          size={16}
                          className="text-slate-500"
                          aria-hidden="true"
                        />
                        <label
                          htmlFor="globalStockDate"
                          className="text-sm font-medium text-gray-600"
                        >
                          Date:
                        </label>
                        <input
                          id="globalStockDate"
                          type="date"
                          value={globalStockDate}
                          max={dayjs().format("YYYY-MM-DD")}
                          onChange={(e) => {
                            setGlobalStockDate(e.target.value);
                            updateDashboardParams({
                              stockDate: e.target.value,
                            });
                          }}
                          className="h-10 min-w-0 rounded-md border border-slate-300 px-2 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                        />
                      </div>
                      <CombinedStockDownloadButton
                        globalDate={globalStockDate}
                      />
                    </div>
                  )}
                </nav>
              </div>

              <div className="min-h-[560px] p-3 sm:p-4 lg:min-h-[720px]">
                {activeStockTab === "product" && (
                  <ProductStockPageByWarehouse globalDate={globalStockDate} />
                )}
                {activeStockTab === "raw" && (
                  <StoreStockPageByWarehouse globalDate={globalStockDate} />
                )}
                {activeStockTab === "gp" && <GPHierarchyDashboard />}
                {activeStockTab === "cost" && <ProductionCostPage />}
              </div>
            </div>
          </div>
        )}
      </div>

      <LossReportModal
        isOpen={isLossReportModalOpen}
        onClose={() => setIsLossReportModalOpen(false)}
      />
    </div>
  );
};

export default AdminBDMDashboard;
